import { createFileRoute } from '@tanstack/react-router';
import { Heading, Text } from 'react-aria-components';
import RootLayout from 'src/components/layout/RootLayout';
import useSkeletonCleanup from 'src/init/useSkeletonCleanup';

const sections = [
  {
    title: 'Dashboard',
    body: 'The Dashboard serves as the main entry point to the Student Center. From here, you can view your active courses, track learning progress, search for course packs, and quickly resume your studies. The dashboard also provides access to course details, recently accessed content, and enrollment options through the Add Course feature.',
  },
  {
    title: 'Course Pack',
    body: 'The Course Pack page provides an overview of a specific course, including course information, progress tracking, and available learning materials. From this page, you can open individual materials, review completion status, and access any password-protected content associated with the course.',
  },
  {
    title: 'Course Pack Reader',
    body: 'The Course Pack Reader allows you to view and study course materials in a dedicated reading environment. You can navigate between pages, adjust zoom levels, search within the document, switch viewing modes, and access study tools without leaving the reader.',
  },
  {
    title: 'Notes & Highlights',
    body: 'Select any text within a document to create highlights and attach personal notes. Highlights can be color-coded and saved for later review. All saved notes appear in the Notes panel, allowing you to quickly revisit important sections of the material.',
  },
  {
    title: 'Flashcards',
    body: 'Create flashcards directly from selected text within the reader. When creating a flashcard, the selected content is automatically added to the front side, while you can customize the back side with your own notes or answers. Flashcards are organized into decks for efficient studying and review.',
  },
  {
    title: 'Document Summary',
    body: 'Use the Summary feature to generate an AI-powered overview of the currently opened course material. The summary highlights key concepts and topics, helping you quickly understand the main points before or after reading the full document.',
  },
  {
    title: 'Ask Paul AI',
    body: 'Ask Paul is an AI-powered study assistant that helps you find information within your course materials. You can ask questions about the content and receive contextual answers based on the currently available course resources, making it easier to locate and understand important information.',
  },
  {
    title: 'Reader Tools',
    body: 'Additional reader tools include text selection, page navigation, page rotation, scrolling preferences, presentation mode, printing, and document layout controls. These features allow you to customize the reading experience according to your study preferences.',
  },
];

const HowToUsePage = () => {
  useSkeletonCleanup();

  return (
    <RootLayout className="h-full flex flex-col pt-4">
      <div className="w-full max-w-[47.5rem] mx-auto bg-surface-alt rounded-3xl shadow-3 p-11 flex-1 min-h-0 overflow-auto">
        <Heading
          level={1}
          className="text-surface-alt-90 text-2xl font-bold mb-6"
        >
          How to Use the LAD Digital Portal
        </Heading>
        <div className="flex flex-col gap-6">
          {sections.map((section) => (
            <div key={section.title}>
              <Heading
                level={2}
                className="text-surface-alt-90 text-lg font-bold mb-2"
              >
                {section.title}
              </Heading>
              <Text
                elementType="p"
                className="text-surface-alt-75 font-medium leading-relaxed"
              >
                {section.body}
              </Text>
            </div>
          ))}
        </div>
      </div>
    </RootLayout>
  );
};

export const Route = createFileRoute('/how-to-use')({
  component: HowToUsePage,
});
